import { Box, Grid } from "@mui/material";
import { useState, useEffect } from "react";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiSelect from "src/components/core-components/mui/ByzMuiSelect";
import { AutocompleteChangeReason, AutocompleteChangeDetails } from "@mui/material";
import { useTranslate } from "src/locales";
import { nurseService } from "src/api/nurse/nurseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";

interface AddNurseInterventionModalProps {
  selectedGuideId: number | null;
  selectedPlanId: number | null;
  onSuccess?: () => void;
  selectedInterventionId?: number;
  selectedPlanName?: string;
  selectedGuideName?: string;
}

interface InterventionType {
  id: number;
  name: string;
  period: number;
  is_active: string;
}

interface GuideType {
  keyid: number;
  name: string;
}

interface PlanType {
  id: number;
  ncpname: string;
}

function AddNurseInterventionModal({
  selectedPlanId,
  onSuccess,
  selectedInterventionId,
  selectedPlanName,
  selectedGuideName,
}: AddNurseInterventionModalProps) {
  const { t } = useTranslate();

  const [name, setName] = useState("");
  const [period, setPeriod] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const periodOptions = [
    { label: "1 saat", value: 60 },
    { label: "2 saat", value: 120 },
    { label: "3 saat", value: 180 },
    { label: "4 saat", value: 240 },
    { label: "6 saat", value: 360 },
    { label: "8 saat", value: 480 },
    { label: "12 saat", value: 720 },
    { label: "24 saat", value: 1440 },
  ];

  const handleSelectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: unknown,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<unknown>
  ) => {
    if (value && typeof value === 'object' && 'label' in value && 'value' in value) {
      setPeriod(value.value as number);
    } else {
      setPeriod(null);
    }
  };

  useEffect(() => {
    const fetchInterventionData = async () => {
      if (!selectedPlanId || !selectedInterventionId) {
        setName("");
        setPeriod(null);
        setIsActive(false);
        return;
      }
      try {
        const found = (await nurseService.nurseCare.getNurseCarePlanDetailsById<{data: InterventionType}>({ id: selectedInterventionId })).data;

        if (found) {
          setName(found.name || "");
          setPeriod(found.period !== undefined && found.period !== null ? Number(found.period.toString().split(" ",1)) : null);          
          setIsActive(found.is_active === "1");
        } else {
          setName("");
          setPeriod(null);
          setIsActive(false);
        }
      } catch (err) {
        console.error("Hemşirelik girişimi bilgisi alınamadı", err);
      }
    };
    fetchInterventionData();
  }, [selectedPlanId, selectedInterventionId]);

  return (
    <Box sx={{ p: 1 }}>
      <Grid sx={{ py: 2, borderBottom: "1px solid gray"}}>
        <ByzNurseSaveButton
          onClick={async () => {
            if (!selectedPlanId || period === null) return;

            try {
              let response: any;
              if (typeof selectedInterventionId === "number" && selectedInterventionId > 0) {
                response = await nurseService.nurseCarePlanDetail.update(
                  { id: selectedInterventionId },
                  {
                    name,
                    period,
                    isactive: isActive ? "1" : "0",
                  }
                );
              } else {
                response = await nurseService.nurseCarePlanDetail.create({
                  ncpid: selectedPlanId,
                  name,
                  period,
                  isactive: isActive ? "1" : "0",
                });
              }
              
              const isSuccess = response?.issuccess !== false;
              
              if (isSuccess) {
                onSuccess?.();
              } else {
                setToastType("error");
                setToastTitle(t("toast.error-title"));
                setToastMessage(response?.message || t("toast.error-content"));
                setToastOpen(true);
              }
            } catch (error) {
              setToastType("error");
              setToastTitle(t("toast.error-title"));
              setToastMessage(t("toast.error-content"));
              setToastOpen(true);
            }
          }}
        />
      </Grid>
      <Grid container columnSpacing={2} alignItems="center" sx={{ borderBottom: "1px solid #E0E0E0", background: "#F4F7FE", py:".6rem" }}>
        <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
          <ByzMuiTypography>{t("nurse.care-plans.selected-guide")}</ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField label={t("nurse.care-plans.selected") } fullWidth value={selectedGuideName} />
        </Grid>
      </Grid>
      <Grid container columnSpacing={2} alignItems="center" sx={{ borderBottom: "1px solid #E0E0E0", background: "#F4F7FE" , py:".5rem"}}>
        <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
          <ByzMuiTypography>{t("nurse.care-plans.selected-plan")}</ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField label={t("nurse.care-plans.selected") } fullWidth value={selectedPlanName} />
        </Grid>
      </Grid>
      <Grid container columnSpacing={2} alignItems="stretch" sx={{ borderBottom: "1px solid #E0E0E0" , py:".5rem"}}>
        <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
          <ByzMuiTypography>{t("nurse.care-plans.plan-name")}</ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiTextField label={t("nurse.care-plans.plan-name") } onChange={(e) => setName(e.target.value)} value={name} fullWidth />
        </Grid>
      </Grid>
      <Grid container columnSpacing={2} alignItems="stretch" sx={{ borderBottom: "1px solid #E0E0E0" , py:".5rem"}}>
        <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
          <ByzMuiTypography>{t("nurse.care-plans.period")}</ByzMuiTypography>
        </Grid>
        <Grid item xs={8}>
          <ByzMuiSelect
            renderInput={(params) => <ByzMuiTextField {...params} variant="outlined" />}
            options={periodOptions}
            onChange={handleSelectChange}
            value={periodOptions.find(option => option.value === period) || null}
            sx={{ width: "100%", maxWidth: "300px", py:".5rem" }}
            color="info"
            label="label"
            placeholder={t("nurse.care-plans.period")}
            multiple={false}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ borderBottom: "1px solid #E0E0E0" }}>
        <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
          <ByzMuiTypography>{t("active")}</ByzMuiTypography>
        </Grid>
        <Grid item xs={8} sx={{ display: "flex", alignItems: "center", p: 2, mt: 1 }}>
          <ByzMuiCheckBox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </Grid>
      </Grid>
      <ByzMuiSnackBar
        open={toastOpen}
        type={toastType}
        title={toastTitle}
        message={toastMessage}
        onClose={() => setToastOpen(false)}
      />
    </Box>
  );
}

export default AddNurseInterventionModal;
